import React, { useRef, useCallback, useState, useEffect, useMemo } from 'react';
import { usePreviewInteraction } from '../../contexts/PreviewInteractionContext';
import {
  computeConstraintGrid,
  snapToGrid,
  shouldUseEdgeSnap,
  computeEdgeSnap,
  computeEdgeSnapX,
} from '../../utils/gridSnap';

/**
 * Get non-overlay children of an element (filters out grid dots and position badges).
 */
function getContentChildren(element) {
  return [...element.children].filter(child =>
    !child.classList.contains('nudge-grid-overlay') &&
    !child.classList.contains('nudge-position-badge')
  );
}

/**
 * Measure actual visible content HORIZONTAL bounds within a container.
 * Recursively dives through single-child wrappers that span full width
 * (common in template layouts: NudgeableGroup → wrapper div → actual content).
 * Returns { left, right, width } in screen coordinates.
 */
function measureContentBounds(element) {
  const containerRect = element.getBoundingClientRect();
  const children = getContentChildren(element);

  if (children.length === 0) {
    return { left: containerRect.left, right: containerRect.right, width: containerRect.width };
  }

  // If single child is ~same width as container, it's a stretchy wrapper — dive deeper
  if (children.length === 1) {
    const childRect = children[0].getBoundingClientRect();
    if (Math.abs(childRect.width - containerRect.width) < 10 && children[0].children.length > 0) {
      return measureContentBounds(children[0]);
    }
  }

  // Multiple children or single smaller child: measure union of all
  let minLeft = Infinity;
  let maxRight = -Infinity;
  for (const child of children) {
    const r = child.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) continue;
    minLeft = Math.min(minLeft, r.left);
    maxRight = Math.max(maxRight, r.right);
  }

  if (minLeft === Infinity) {
    return { left: containerRect.left, right: containerRect.right, width: containerRect.width };
  }
  return { left: minLeft, right: maxRight, width: maxRight - minLeft };
}

/**
 * Measure actual visible content VERTICAL bounds within a container.
 * Parallels measureContentBounds but for the Y axis.
 * Dives through wrappers that fill the container height.
 * Returns { top, bottom, height } in screen coordinates.
 *
 * This fixes logo snapping: logos inside padded NudgeableGroups (e.g., pt-[72px])
 * should snap based on the actual logo position, not the container + padding.
 *
 * Uses a 70% fill threshold: if content fills >70% of container height,
 * the container IS the visual block (e.g., colored strips with py padding).
 * If content fills <70%, container has positioning-only padding (e.g., logo offset).
 */
function measureContentVerticalBounds(element) {
  const containerRect = element.getBoundingClientRect();
  const children = getContentChildren(element);

  if (children.length === 0) {
    return { top: containerRect.top, bottom: containerRect.bottom, height: containerRect.height };
  }

  // If single child fills container height, it's a stretchy wrapper — dive deeper
  if (children.length === 1) {
    const childRect = children[0].getBoundingClientRect();
    if (Math.abs(childRect.height - containerRect.height) < 10 && children[0].children.length > 0) {
      return measureContentVerticalBounds(children[0]);
    }
  }

  // Measure vertical extent of all visible children
  let minTop = Infinity;
  let maxBottom = -Infinity;
  for (const child of children) {
    const r = child.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) continue;
    minTop = Math.min(minTop, r.top);
    maxBottom = Math.max(maxBottom, r.bottom);
  }

  if (minTop === Infinity) {
    return { top: containerRect.top, bottom: containerRect.bottom, height: containerRect.height };
  }

  const contentHeight = maxBottom - minTop;

  // If content fills most of container (>70%), the container IS the visual block
  // (padding is part of the design — e.g., colored strips, glass cards).
  // If content is much smaller (<70%), container has positioning-only padding
  // (e.g., logo with pt-[72px] where only the logo should snap to guides).
  if (containerRect.height > 0 && contentHeight > containerRect.height * 0.7) {
    return { top: containerRect.top, bottom: containerRect.bottom, height: containerRect.height };
  }

  return { top: minTop, bottom: maxBottom, height: contentHeight };
}


/**
 * NudgeableGroup — makes a content group draggable within template constraints.
 *
 * Two snap modes (automatic based on constraint range):
 *
 * 1. **Edge-aware free-form** (content + logo nudge, large ranges >80px):
 *    Block moves freely. Snaps when top/center/bottom edge approaches
 *    a slide guide line (center, thirds, margins). Guide lines highlight
 *    on the slide when snapping. See docs/design/edge-aware-nudge-guides.md.
 *
 * 2. **Grid snap** (small ranges ≤80px):
 *    Block snaps to discrete evenly-spaced positions. Grid dots shown.
 *
 * Click vs Drag disambiguation:
 *   - Movement < 5 canvas px → treated as click (propagates to children)
 *   - Movement >= 5 canvas px → treated as drag (starts nudge)
 */
export function NudgeableGroup({
  groupId,
  children,
  className = '',
  style = {},
}) {
  const {
    interactive,
    editingField,
    scale,
    layoutNudge,
    onNudgeEnd,
    nudgeConstraints,
  } = usePreviewInteraction();

  const containerRef = useRef(null);

  // Get constraints for this group from template definition
  const constraints = nudgeConstraints?.[groupId] || null;

  // Get current nudge for this group from slide data
  const nudgeDx = layoutNudge?.[groupId]?.dx || 0;
  const nudgeDy = layoutNudge?.[groupId]?.dy || 0;
  const currentNudge = useMemo(() => ({ dx: nudgeDx, dy: nudgeDy }), [nudgeDx, nudgeDy]);

  // Determine snap mode
  const useEdgeSnap = useMemo(() => shouldUseEdgeSnap(constraints), [constraints]);

  // Grid positions for grid-snap mode (logo nudge)
  const grid = useMemo(
    () => useEdgeSnap ? { x: [0], y: [0] } : computeConstraintGrid(constraints),
    [constraints, useEdgeSnap]
  );

  // Drag state
  const [isDragging, setIsDragging] = useState(false);
  const [renderOffset, setRenderOffset] = useState({ dx: 0, dy: 0 });
  const [snapInfo, setSnapInfo] = useState(null);   // { guideIds[], edge, label }
  const [badgeBelow, setBadgeBelow] = useState(false); // true = badge below block
  const [badgeLeftPx, setBadgeLeftPx] = useState(null); // null = centered, number = absolute left within group
  const dragStateRef = useRef({
    active: false,
    startClientX: 0,
    startClientY: 0,
    startNudgeDx: 0,
    startNudgeDy: 0,
    currentDx: 0,
    currentDy: 0,
    hasMoved: false,
    // Edge-snap measurements (set at drag start)
    baseTop: 0,
    blockHeight: 0,
    baseLeft: 0,
    blockWidth: 0,
    canvasRootEl: null,
  });

  const effectiveScale = scale || 0.35;

  // ── Document-level mouse move handler ────────────────────────────────
  useEffect(() => {
    const onMouseMove = (e) => {
      const state = dragStateRef.current;
      if (!state.active) return;

      const deltaX = (e.clientX - state.startClientX) / effectiveScale;
      const deltaY = (e.clientY - state.startClientY) / effectiveScale;
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

      // Threshold: 5 canvas pixels to start drag
      if (!state.hasMoved && distance < 5) return;

      if (!state.hasMoved) {
        state.hasMoved = true;
        setIsDragging(true);
      }

      const rawDx = state.startNudgeDx + deltaX;
      const rawDy = state.startNudgeDy + deltaY;

      let snappedDx, snappedDy;
      let snapY = null; // { guideId, edge, label }
      let snapX = null; // { guideId, edge, label }

      if (useEdgeSnap) {
        // ── Edge-aware free-form snapping ──────────────────────────
        const rangeY = constraints?.range?.y || [0, 0];
        const rangeX = constraints?.range?.x || [0, 0];
        const axis = constraints?.axis || 'y';

        if (axis === 'y' || axis === 'both') {
          const result = computeEdgeSnap(rawDy, state.baseTop, state.blockHeight, rangeY);
          snappedDy = result.dy;
          if (result.snapped) {
            snapY = { guideId: result.guideId, edge: result.edge, label: result.label };
          }
        } else {
          snappedDy = 0;
        }

        if (axis === 'x' || axis === 'both') {
          const resultX = computeEdgeSnapX(rawDx, state.baseLeft, state.blockWidth, rangeX);
          snappedDx = resultX.dx;
          if (resultX.snapped) {
            snapX = { guideId: resultX.guideId, edge: resultX.edge, label: resultX.label };
          }
        } else {
          snappedDx = 0;
        }

        // ── Build combined snap info ────────────────────────────────
        // Collect ALL matching guide IDs from both axes (for multi-highlight in corners)
        const guideIds = [];
        let currentSnapInfo = null;

        if (snapY && snapY.guideId !== 'home') {
          for (const gid of (snapY.allGuideIds || [snapY.guideId])) guideIds.push(gid);
        }
        if (snapX && snapX.guideId !== 'home') {
          for (const gid of (snapX.allGuideIds || [snapX.guideId])) guideIds.push(gid);
        }

        // Both axes at home
        if (snappedDx === 0 && snappedDy === 0 && guideIds.length === 0) {
          const isNearHome =
            Math.abs(Math.max(rangeY[0], Math.min(rangeY[1], rawDy))) < 15 ||
            Math.abs(Math.max(rangeX[0], Math.min(rangeX[1], rawDx))) < 15;
          if (isNearHome) {
            currentSnapInfo = { guideIds: [], edge: 'home', label: 'home' };
          }
        }

        // Non-home snaps: pick the best label, collect all guide IDs
        if (!currentSnapInfo && guideIds.length > 0) {
          // Prefer non-home snap for label display
          const primary = (snapY && snapY.guideId !== 'home') ? snapY
            : (snapX && snapX.guideId !== 'home') ? snapX
            : snapY || snapX;
          currentSnapInfo = {
            guideIds,
            edge: primary.edge,
            label: primary.label,
          };
        }

        // ── Update canvas root for guide highlighting ───────────────
        if (state.canvasRootEl) {
          if (guideIds.length > 0) {
            // Space-separated list — CSS uses [data-snap-guide~="id"] to match
            state.canvasRootEl.setAttribute('data-snap-guide', guideIds.join(' '));
          } else {
            state.canvasRootEl.removeAttribute('data-snap-guide');
          }
        }

        setSnapInfo(currentSnapInfo);
      } else {
        // ── Grid snap (logo nudge) ─────────────────────────────────
        snappedDx = snapToGrid(rawDx, grid.x);
        snappedDy = snapToGrid(rawDy, grid.y);
        setSnapInfo(null);
      }

      state.currentDx = snappedDx;
      state.currentDy = snappedDy;
      setRenderOffset({ dx: snappedDx, dy: snappedDy });

      // ── Badge position: stay within canvas bounds ──────────────────
      const currentTop = state.baseTop + snappedDy;
      setBadgeBelow(currentTop < 70);

      // Horizontal: clamp badge center so it doesn't clip at canvas edges
      const blockLeftInCanvas = state.baseLeft + snappedDx;
      const badgeCenterInCanvas = blockLeftInCanvas + state.blockWidth / 2;
      const BADGE_HALF_W = 120; // estimated half-width of badge in canvas px
      const clampedCenter = Math.max(BADGE_HALF_W, Math.min(1080 - BADGE_HALF_W, badgeCenterInCanvas));
      const idealCenter = state.blockWidth / 2;
      const shift = clampedCenter - badgeCenterInCanvas;
      // Only set custom left when the badge needs to shift
      setBadgeLeftPx(Math.abs(shift) > 5 ? idealCenter + shift : null);
    };

    const onMouseUp = () => {
      const state = dragStateRef.current;
      if (!state.active) return;

      if (state.hasMoved && onNudgeEnd) {
        onNudgeEnd(groupId, { dx: state.currentDx, dy: state.currentDy });
      }

      // Clean up guide highlighting
      if (state.canvasRootEl) {
        state.canvasRootEl.removeAttribute('data-snap-guide');
      }

      state.active = false;
      state.hasMoved = false;
      state.canvasRootEl = null;
      setIsDragging(false);
      setRenderOffset({ dx: 0, dy: 0 });
      setSnapInfo(null);
      setBadgeBelow(false);
      setBadgeLeftPx(null);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);

    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, [effectiveScale, grid, onNudgeEnd, groupId, useEdgeSnap, constraints]);

  // ── Mouse down — begin potential drag ────────────────────────────────
  const handleMouseDown = useCallback((e) => {
    if (!interactive || !constraints || !onNudgeEnd) return;
    if (editingField) return;
    if (e.button !== 0) return;

    const target = e.target;
    if (target.isContentEditable || target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
      return;
    }

    const state = dragStateRef.current;
    state.active = true;
    state.startClientX = e.clientX;
    state.startClientY = e.clientY;
    state.startNudgeDx = currentNudge.dx || 0;
    state.startNudgeDy = currentNudge.dy || 0;
    state.currentDx = currentNudge.dx || 0;
    state.currentDy = currentNudge.dy || 0;
    state.hasMoved = false;

    // ── Measure block for edge-aware snapping ──────────────────────
    if (useEdgeSnap && containerRef.current) {
      const canvasRoot = containerRef.current.closest('.slide-canvas-root');
      state.canvasRootEl = canvasRoot;

      if (canvasRoot) {
        const canvasRect = canvasRoot.getBoundingClientRect();
        // Compute actual rendered scale from the canvas root
        const actualScale = canvasRect.height / 1350;

        // For height: measure actual visible content, not the full container.
        // This fixes logos in padded NudgeableGroups (e.g., pt-[72px]) where
        // container height includes padding but the logo is the only visible element.
        const contentVertical = measureContentVerticalBounds(containerRef.current);
        state.baseTop = (contentVertical.top - canvasRect.top) / actualScale - (currentNudge.dy || 0);
        state.blockHeight = contentVertical.height / actualScale;

        // For width: measure actual visible content, not the full container.
        // Dive through single-child wrappers to find the real content bounds.
        const contentBounds = measureContentBounds(containerRef.current);
        state.baseLeft = (contentBounds.left - canvasRect.left) / actualScale - (currentNudge.dx || 0);
        state.blockWidth = contentBounds.width / actualScale;
      }
    }
  }, [interactive, constraints, onNudgeEnd, editingField, currentNudge, useEdgeSnap]);

  // ── Compute final transform ──────────────────────────────────────────
  const tx = isDragging ? renderOffset.dx : (currentNudge.dx || 0);
  const ty = isDragging ? renderOffset.dy : (currentNudge.dy || 0);
  const hasOffset = tx !== 0 || ty !== 0;
  const transform = hasOffset ? `translate(${tx}px, ${ty}px)` : undefined;

  const isNudgeable = interactive && constraints && onNudgeEnd && !editingField;

  // ── Grid dots for logo nudge (small ranges) ──────────────────────────
  const gridDots = useMemo(() => {
    if (!constraints || useEdgeSnap) return [];
    const axis = constraints.axis || 'y';
    const dots = [];

    if (axis === 'y') {
      for (const y of grid.y) dots.push({ x: 0, y });
    } else if (axis === 'x') {
      for (const x of grid.x) dots.push({ x, y: 0 });
    } else {
      for (const y of grid.y) {
        for (const x of grid.x) dots.push({ x, y });
      }
    }
    return dots;
  }, [constraints, grid, useEdgeSnap]);

  // ── Badge text ───────────────────────────────────────────────────────
  const badgeText = useMemo(() => {
    if (!isDragging) return '';
    if (snapInfo) {
      if (snapInfo.edge === 'home') return '● home';
      const arrow = snapInfo.edge === 'top' ? '⬆'
        : snapInfo.edge === 'bottom' ? '⬇'
        : snapInfo.edge === 'left' ? '◀'
        : snapInfo.edge === 'right' ? '▶'
        : '●';
      return `${arrow} ${snapInfo.edge} → ${snapInfo.label}`;
    }
    if (useEdgeSnap) return `${Math.round(renderOffset.dy)}`;
    if (constraints?.axis === 'both') return `${renderOffset.dx}, ${renderOffset.dy}`;
    return `${renderOffset.dy}`;
  }, [isDragging, snapInfo, useEdgeSnap, renderOffset, constraints]);

  return (
    <div
      ref={containerRef}
      className={`nudgeable-group ${isNudgeable ? 'nudgeable-active' : ''} ${isDragging ? 'nudgeable-dragging' : ''} ${className}`}
      style={{
        ...style,
        transform,
        willChange: isDragging ? 'transform' : undefined,
      }}
      onMouseDown={isNudgeable ? handleMouseDown : undefined}
    >
      {children}

      {/* ── Grid dots (logo nudge only) ──────────────────────────────── */}
      {isDragging && !useEdgeSnap && (
        <div className="nudge-grid-overlay" style={{ pointerEvents: 'none' }}>
          {gridDots.map((dot, i) => {
            const isActive = dot.x === renderOffset.dx && dot.y === renderOffset.dy;
            const isOrigin = dot.x === 0 && dot.y === 0;
            return (
              <div
                key={i}
                className={`nudge-grid-dot ${isActive ? 'nudge-grid-dot-active' : ''} ${isOrigin ? 'nudge-grid-dot-origin' : ''}`}
                style={{
                  transform: `translate(${dot.x - renderOffset.dx}px, ${dot.y - renderOffset.dy}px)`,
                }}
              />
            );
          })}
        </div>
      )}

      {/* ── Position badge ───────────────────────────────────────────── */}
      {isDragging && (
        <div
          className={`nudge-position-badge${badgeBelow ? ' nudge-position-badge-below' : ''}`}
          style={badgeLeftPx != null ? { left: `${badgeLeftPx}px` } : undefined}
        >
          {badgeText}
        </div>
      )}
    </div>
  );
}
